/**
 * Serializer factory registry - breaks circular dependencies between
 * prefab.ts and serializer.ts by providing a registration point for
 * Loader and Dumper constructors.
 */

import type { DumperFlags, DumperOptions, DumpValueResult, LoaderFlags, LoaderOptions, SerializedData } from './types.js';

// ============ Loader Factory ============

export interface ILoader {
  load(data: SerializedData): any;
  loadAsync(data: SerializedData): Promise<any>;
  loadValue(key: string, target?: any, metadata?: PropertyMetadata): any;
  loadProperties(obj: any): void;
  loadToInstance(data: SerializedData, instance: any): any;
  loadToInstanceAsync(data: SerializedData, instance: any): Promise<any>;
  hasFlags(flags: LoaderFlags): boolean;
  errors: any[];
}

// Forward declaration of PropertyMetadata to avoid circular import
// The actual type will be resolved at usage sites
interface PropertyMetadata {
  deserialize?: (data: any, loader: ILoader) => any;
  editorOnly?: boolean;
  migrateFrom?: string;
  resolveArrayElement?: (data: any, existingArray: any[], index: number) => any | undefined;
  transient?: boolean;
}

type LoaderCtor = new (options?: LoaderOptions) => ILoader;

let loaderCtor: LoaderCtor | undefined;

export function registerLoader(ctor: LoaderCtor): void {
  loaderCtor = ctor;
}

export function createLoader(options?: LoaderOptions): ILoader {
  if (!loaderCtor) {
    throw new Error('Loader not registered. Ensure serializer module is imported.');
  }
  return new loaderCtor(options);
}

// ============ Dumper Factory ============

export interface IDumper {
  dump(obj: any): SerializedData;
  dumpValue(key: string, value: any, instanceKey?: string, metadata?: PropertyMetadata): DumpValueResult;
  dumpProperties(obj: any): void;
  markEditorOnly(): void;
  hasFlags(flags: DumperFlags): boolean;
  dumpingAsPrefab: boolean;
  removeDumpedValue(key: string): void;
}

type DumperCtor = new (options?: DumperOptions) => IDumper;

let dumperCtor: DumperCtor | undefined;

export function registerDumper(ctor: DumperCtor): void {
  dumperCtor = ctor;
}

export function createDumper(options?: DumperOptions): IDumper {
  if (!dumperCtor) {
    throw new Error('Dumper not registered. Ensure serializer module is imported.');
  }
  return new dumperCtor(options);
}
